import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ExternalLink, BookOpen, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferenceLibraryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/references`)
      .then((r) => alive && setItems(r.data.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const filtered = items.filter((it) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      it.name.toLowerCase().includes(q) ||
      (it.subtitle || '').toLowerCase().includes(q) ||
      (it.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  return (
    <div className="min-h-screen" data-testid="reference-library-page">
      <SEO
        title="Reference Library — Peptide Research Sources"
        description="Curated peer-reviewed research and pharmacology references for every peptide in the Wilvada catalog. Sourced from PubMed (NIH), PubChem (NCBI), and DrugBank."
      />
      <div className="bg-[hsl(210,52%,16%)] text-slate-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="h-6 w-6 text-[hsl(210,40%,75%)]" />
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(210,40%,75%)] font-body">Educational Hub</p>
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light font-heading">Reference Library</h1>
          <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-2xl font-body leading-relaxed">
            Curated peer-reviewed research and pharmacology data for every peptide in our catalog. Sources include
            <span className="text-white"> PubMed</span> (U.S. National Library of Medicine),
            <span className="text-white"> PubChem</span> (NCBI), and
            <span className="text-white"> DrugBank</span>. For research use only — not medical advice.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-8 max-w-md">
          <Input
            placeholder="Search by peptide name or pathway…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="reference-search-input"
          />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading references…</div>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground text-sm font-body">No references match your search.</p>
        ) : (
          <div className="space-y-6">
            {filtered.map((it) => (
              <Card key={it.product_id} data-testid={`reference-group-${it.product_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="font-heading text-xl">
                        <Link
                          to={`/product/${it.product_id}`}
                          className="hover:text-[hsl(210,45%,40%)] transition-colors"
                          data-testid={`reference-product-link-${it.product_id}`}
                        >
                          {it.name}
                        </Link>
                      </CardTitle>
                      {it.subtitle && (
                        <p className="text-sm text-muted-foreground font-body mt-1">{it.subtitle}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(it.tags || []).map((t) => (
                        <Badge key={t} variant="secondary" className="text-[10px] uppercase tracking-wider">{t}</Badge>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {it.references.map((ref, idx) => (
                      <li key={`${ref.url || ref.source || 'ref'}-${idx}`}>
                        <a
                          href={ref.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="block group rounded-md border border-border hover:border-[hsl(210,45%,40%)]/50 hover:shadow-sm transition-all p-3 h-full"
                          data-testid={`reference-link-${it.product_id}-${idx}`}
                        >
                          <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[hsl(210,45%,40%)] font-body">{ref.source}</p>
                          <p className="text-sm font-medium text-foreground mt-1 leading-snug">
                            {ref.title}
                            <ExternalLink className="inline h-3 w-3 ml-1 opacity-50 group-hover:opacity-100" />
                          </p>
                          {ref.description && (
                            <p className="text-xs text-muted-foreground mt-1 leading-snug font-body">{ref.description}</p>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="mt-12 text-xs text-muted-foreground italic font-body max-w-3xl">
          The Reference Library is provided for educational and informational purposes only. It does not constitute medical
          advice and is not a substitute for consultation with a qualified researcher or licensed professional. All Wilvada LC
          products are intended strictly for laboratory research and development purposes — not for human or veterinary use.
        </p>
      </div>
    </div>
  );
}
